import { UserInfoWrapper } from "@/components/UserInfo/UserInfoWrapper";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { AddEditIntakeApproverModal } from "@/containers/Admin/ProjectRoles/components/RegionalRole/Modals";
import { FieldNames, FormType } from "@/enums";
import { nameof } from "@/helpers";
import { IUser } from "@/interfaces";
import { IIntakeApprover } from "@/interfaces/Project/Roles/IIntakeApprover";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetUsersByIdsMutation } from "@/services";
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table, Tag, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  regionRole: IRegionalProjectRole;
}

export const IntakeApproversTab = ({ regionRole }: IProps) => {
  const intl = useIntl();

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { updateRegionalRole } = useProjectRoleContext();

  const [getUsersByIds, { data: usersByIds, isLoading }] =
    useGetUsersByIdsMutation();

  const [approvers, setApprovers] = useState<IIntakeApprover[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [workingIntakeApprover, setWorkingIntakeApprover] =
    useState<string>("");

  useEffect(() => {
    if (!approvers) return;

    const userIds = approvers.map((user) => user.userId);
    getUsersByIds(userIds);
  }, [approvers, getUsersByIds]);

  useEffect(() => {
    if (!regionRole?.intakeApprovers) return;

    setApprovers(regionRole.intakeApprovers);
  }, [regionRole?.intakeApprovers]);

  const addApprover = (newUser: IIntakeApprover): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.intakeApprovers = [newUser, ...approvers];

    if (workingIntakeApprover !== "") {
      updatedRegionRole.intakeApprovers = [
        newUser,
        ...approvers.filter((x) => x.id !== workingIntakeApprover),
      ];
    } else {
      updatedRegionRole.intakeApprovers = [newUser, ...approvers];
    }

    updateRegionalRole(updatedRegionRole);
    setModalOpen(false);
  };

  const deleteApprover = (id: string): void => {
    const updatedRegionRole = { ...regionRole };
    updatedRegionRole.intakeApprovers = approvers.filter(
      (approver) => approver?.id !== id
    );

    updateRegionalRole(updatedRegionRole);
  };

  const handleAddAnother = (newUser: IIntakeApprover): void => {
    addApprover(newUser);
    setModalOpen(true);
  };
  const editApprover = (id: string): void => {
    setWorkingIntakeApprover(id);
    setModalOpen(true);
  };
  const closeApprover = (): void => {
    setWorkingIntakeApprover("");
    setModalOpen(false);
  };

  const showAddApproverModal = (): void => {
    setWorkingIntakeApprover("");
    setModalOpen(true);
  };

  const columns: ColumnsType<IIntakeApprover> = [
    {
      title: intl.formatMessage({ id: "projectRoles.name" }),
      dataIndex: nameof<IIntakeApprover>("userId"),
      render: (userId) => {
        const user = usersByIds?.find((x: IUser) => x.id === userId);
        return (
          <UserInfoWrapper user={user}>{user?.displayName}</UserInfoWrapper>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "projectRoles.productCategory" }),
      dataIndex: nameof<IIntakeApprover>("publicProductCategoryIds"),
      render: (publicProductCategoryIds) =>
        publicProductCategoryIds?.map((tag: string) => (
          <Tag color={"blue"} key={tag}>
            {getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductCategory,
              tag
            )}
          </Tag>
        )),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.action" }),
      dataIndex: nameof<IIntakeApprover>("id"),
      render: (id) => (
        <>
          <Button
            data-test-id={`intake-approvers-edit-${id}`}
            type="link"
            onClick={(): void => editApprover(id)}
            icon={<FontAwesomeIcon icon={faEdit} />}
          ></Button>
          <Button
            data-test-id={`intake-approvers-delete-${id}`}
            type="link"
            onClick={(): void => deleteApprover(id)}
            icon={<FontAwesomeIcon icon={faTrash} />}
          ></Button>
        </>
      ),
    },
  ];

  return (
    <>
      <Row justify="space-between" className="pl-1">
        <Col>
          <Typography.Title level={5}>
            {intl.formatMessage({ id: "projectRoles.intakeApproversTab" })}
          </Typography.Title>
        </Col>
        <Col>
          <Button
            data-test-id="add-intake-approver"
            onClick={(): void => showAddApproverModal()}
          >
            {intl.formatMessage({ id: "projectRoles.addUser" })}
          </Button>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        columns={columns}
        dataSource={approvers}
        rowKey={({ id }) => id}
      />
      <AddEditIntakeApproverModal
        regionRole={regionRole}
        open={modalOpen}
        onClose={(): void => closeApprover()}
        onAddAnother={handleAddAnother}
        onOk={addApprover}
        intakeApproverId={workingIntakeApprover}
      />
    </>
  );
};
